import storage from 'local-storage-fallback';

import { debounce } from '../lib/utils';
import { LS_MSG_DRAFTS_KEY } from '../lib/constants';
import { useCallback, useMemo } from 'react';

export const useMessageDraft = () => {
  const saveDraft = useCallback((chatId: string, message: string) => {
    try {
      const drafts = JSON.parse(storage.getItem(LS_MSG_DRAFTS_KEY) || '{}');
      if (message.trim()) {
        drafts[chatId] = message;
      } else {
        delete drafts[chatId];
      }
      storage.setItem(LS_MSG_DRAFTS_KEY, JSON.stringify(drafts));
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[DEV] Failed to save draft:', err);
    }
  }, []);
  
  const loadDraft = useCallback((chatId: string): string => {
    try {
      const drafts = JSON.parse(storage.getItem(LS_MSG_DRAFTS_KEY) || '{}');
      return drafts[chatId] || '';
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[DEV] Failed to load draft:', err);
      return '';
    }
  }, []);
  
  // Debounced version of saveDraft to avoid excessive localStorage writes
  // Memoize to prevent recreation on every render
  const debouncedSaveDraft = useMemo(() => debounce(saveDraft, 500), [saveDraft]);
  
  const deleteDraft = useCallback((chatId: string) => {
    try {
      const drafts = JSON.parse(storage.getItem(LS_MSG_DRAFTS_KEY) || '{}');
      delete drafts[chatId];
      storage.setItem(LS_MSG_DRAFTS_KEY, JSON.stringify(drafts));
      debouncedSaveDraft(chatId, ''); // ensure any pending debounced saveDraft are flushed
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[DEV] Failed to delete draft:', err);
    }
  }, [debouncedSaveDraft]);

  return {
    saveDraft,
    loadDraft,
    deleteDraft,
    debouncedSaveDraft,
  };
}; 